// utils/releaseVersions.js
// Utilities for managing app version/release tracking
import { db } from "../firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  query,
  orderBy,
  where,
} from "firebase/firestore";

const VERSIONS_COLLECTION = "releaseVersions";
const COUNTERS_DOC = "counters";

/**
 * Get the next available version ID (auto-incrementing)
 */
export async function getNextVersionId() {
  try {
    const countersRef = doc(db, VERSIONS_COLLECTION, COUNTERS_DOC);
    const counterDoc = await getDoc(countersRef);

    if (!counterDoc.exists()) {
      // Initialize counter
      await setDoc(countersRef, { versionId: 1 });
      return 1;
    }

    const current = counterDoc.data().versionId || 0;
    const next = current + 1;
    await updateDoc(countersRef, { versionId: next });
    return next;
  } catch (err) {
    console.error("Error getting next version ID:", err);
    throw err;
  }
}

/**
 * Create a new version/release entry
 */
export async function createVersionEntry({
  version, // e.g., "2.0.89"
  releaseDate, // Date or string
  changes = [], // Array of change objects: { type: "bug"|"enhancement"|"other", description: string, logEntryId?: string }
  notes = "",
  createdBy = "system",
}) {
  try {
    const versionId = await getNextVersionId();
    const id = `v${String(versionId).padStart(3, "0")}`;

    const versionData = {
      id,
      version,
      releaseDate: releaseDate instanceof Date ? releaseDate : new Date(releaseDate),
      changes,
      notes,
      createdBy,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(doc(db, VERSIONS_COLLECTION, id), versionData);
    return { id, ...versionData };
  } catch (err) {
    console.error("Error creating version entry:", err);
    throw err;
  }
}

/**
 * Update an existing version entry
 */
export async function updateVersionEntry(versionId, updates) {
  try {
    const versionRef = doc(db, VERSIONS_COLLECTION, versionId);
    const updateData = {
      ...updates,
      updatedAt: serverTimestamp(),
    };
    await updateDoc(versionRef, updateData);
  } catch (err) {
    console.error("Error updating version entry:", err);
    throw err;
  }
}

/**
 * Delete a version entry
 */
export async function deleteVersionEntry(versionId) {
  try {
    await deleteDoc(doc(db, VERSIONS_COLLECTION, versionId));
  } catch (err) {
    console.error("Error deleting version entry:", err);
    throw err;
  }
}

/**
 * Get a single version entry by ID
 */
export async function getVersionEntry(versionId) {
  try {
    const versionDoc = await getDoc(doc(db, VERSIONS_COLLECTION, versionId));
    if (!versionDoc.exists()) {
      return null;
    }
    const data = versionDoc.data();
    return {
      ...data,
      releaseDate: data.releaseDate?.toDate?.() || new Date(data.releaseDate),
      createdAt: data.createdAt?.toDate?.() || new Date(data.createdAt),
      updatedAt: data.updatedAt?.toDate?.() || new Date(data.updatedAt),
    };
  } catch (err) {
    console.error("Error getting version entry:", err);
    throw err;
  }
}

/**
 * Get all version entries, ordered by release date (newest first)
 */
export async function getAllVersions() {
  try {
    const q = query(collection(db, VERSIONS_COLLECTION), orderBy("releaseDate", "desc"));
    const snapshot = await getDocs(q);
    const versions = [];
    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      versions.push({
        id: docSnap.id,
        ...data,
        releaseDate: data.releaseDate?.toDate?.() || new Date(data.releaseDate),
        createdAt: data.createdAt?.toDate?.() || new Date(data.createdAt),
        updatedAt: data.updatedAt?.toDate?.() || new Date(data.updatedAt),
      });
    });
    return versions;
  } catch (err) {
    console.error("Error getting all versions:", err);
    throw err;
  }
}

/**
 * Add a change to an existing version
 */
export async function addVersionChange(versionId, change) {
  try {
    const versionRef = doc(db, VERSIONS_COLLECTION, versionId);
    const versionDoc = await getDoc(versionRef);
    if (!versionDoc.exists()) {
      throw new Error("Version entry not found");
    }
    const currentData = versionDoc.data();
    const changes = Array.isArray(currentData.changes) ? currentData.changes : [];
    changes.push(change);
    await updateVersionEntry(versionId, { changes });
  } catch (err) {
    console.error("Error adding version change:", err);
    throw err;
  }
}

/**
 * Increment a version number (e.g., "2.0.89" -> "2.0.90")
 * @param {string} currentVersion - Current version string (e.g., "2.0.89")
 * @param {string} incrementType - "patch", "minor", or "major" (default: "patch")
 * @returns {string} - New version string
 */
export function incrementVersion(currentVersion, incrementType = "patch") {
  if (!currentVersion || typeof currentVersion !== "string") {
    throw new Error("Invalid version string");
  }
  
  const parts = currentVersion.split(".").map((p) => parseInt(p, 10));
  if (parts.length < 2 || parts.some((p) => isNaN(p))) {
    throw new Error(`Invalid version format: ${currentVersion}`);
  }
  
  // Ensure we have at least major.minor.patch
  while (parts.length < 3) {
    parts.push(0);
  }
  
  if (incrementType === "major") {
    parts[0] += 1;
    parts[1] = 0;
    parts[2] = 0;
  } else if (incrementType === "minor") {
    parts[1] += 1;
    parts[2] = 0;
  } else {
    // patch (default)
    parts[2] += 1;
  }
  
  return parts.join(".");
}
